import { Bell, LayoutDashboard, LogOut, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/lib/auth/useAuth';
import { useNotifications } from '@/features/notifications/api';

/**
 * Corner user menu. Click to open (reliable item selection — hover menus drop
 * clicks as the menu closes mid-move). Radix handles keyboard + outside-click.
 */
export function UserMenu() {
  const { user, isStaff, logout } = useAuth();
  const notifications = useNotifications();
  const unread = notifications.data?.unread_count ?? 0;
  const navigate = useNavigate();

  // Staff live inside the admin shell (sidebar); members use the public chrome.
  const profilePath = isStaff ? '/admin/profile' : '/profile';
  const notificationsPath = isStaff ? '/admin/notifications' : '/notifications';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Account menu" className="relative">
          {user?.avatar_url ? (
            <img
              src={user.avatar_url}
              alt=""
              className="h-7 w-7 rounded-full object-cover ring-1 ring-line"
            />
          ) : (
            <User className="h-5 w-5" />
          )}
          {unread > 0 && (
            <span className="absolute right-1 top-1 h-2.5 w-2.5 rounded-full bg-pink-500 ring-2 ring-surface" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent sideOffset={6}>
        <div className="border-b border-line px-2 pb-2 pt-1">
          <p className="truncate text-sm font-semibold text-ink">{user?.name}</p>
          <p className="truncate text-xs text-muted">{user?.email}</p>
        </div>
        <DropdownMenuItem onSelect={() => navigate(profilePath)}>
          <User className="h-4 w-4" /> Profile
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigate(notificationsPath)}>
          <Bell className="h-4 w-4" />
          <span className="flex-1">Notifications</span>
          {unread > 0 && (
            <span className="rounded-full bg-pink-500 px-1.5 py-0.5 text-xs font-bold text-white">
              {unread}
            </span>
          )}
        </DropdownMenuItem>
        {isStaff && (
          <DropdownMenuItem onSelect={() => navigate('/admin')}>
            <LayoutDashboard className="h-4 w-4" /> Admin dashboard
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={() => void logout()}>
          <LogOut className="h-4 w-4" /> Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
